import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import DoorFront from "@mui/icons-material/DoorFrontOutlined";
import EventSeatIcon from "@mui/icons-material/EventSeat";
import LocalGasStationIcon from "@mui/icons-material/LocalGasStation";
import SettingsIcon from "@mui/icons-material/Settings";
import { useTranslation } from "react-i18next";
// components
import AppButton from "../../../../components/ui/AppButton";

type CarCategory = {
  acriss_code: string;
  sample: string;
  seats: number;
  doors: number;
  transmission: string;
  fuel_type: string;
  image_url: string;
  base_price: number;
  final_price: number;
  currency: string;
  is_bookable: boolean;
};

type EuropcarCarCategoriesListProps = {
  carCategories: CarCategory[];
  onSelect?: (car: CarCategory) => void;
  tenantInfo: any;
  selectedCar?: CarCategory | null;
};

export default function EuropcarCarCategoriesList({
  carCategories,
  onSelect,
  tenantInfo,
  selectedCar
}: EuropcarCarCategoriesListProps) {
  const { t } = useTranslation();
  if (!carCategories || carCategories.length === 0) return null;

  return (
    <Grid container spacing={3}>
      {carCategories.map(car => {
        const isDisabled = !car.is_bookable;
        const isSelected = selectedCar?.acriss_code === car.acriss_code;

        return (
          <Grid item xs={12} md={6} key={car.acriss_code}>
            <Box
              sx={{
                border: "1px solid",
                borderColor: isSelected ? tenantInfo?.style?.button?.color : "#efefef",
                borderRadius: 1,
                p: 2,
                display: "flex",
                gap: 1,
                opacity: isDisabled ? 0.5 : 1,
                transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                boxShadow: isSelected ? "0 0 0 1px rgba(46, 125, 50, 0.3)" : "none"
              }}
              data-cy={`europcar-car-card-${car.acriss_code}`}
            >
              {/* IMAGE */}
              <Box sx={{ width: { xs: 125, sm: 130, lg: 150 }, flexShrink: 0 }}>
                <img src={car?.image_url} alt={car?.sample} style={{ width: "100%", objectFit: "contain" }} />
              </Box>

              {/* CONTENT */}
              <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                <Box sx={{ fontSize: 13, fontFamily: "Neutraface2Demi" }}>{car.sample}</Box>

                <Box sx={{ fontSize: 11, color: "text.secondary", mb: 1 }}>
                  {car?.transmission} • {car?.fuel_type}
                </Box>

                {/* FEATURES */}
                <Box sx={{ display: "flex", gap: 1, fontSize: 11, alignItems: "center" }}>
                  <DoorFront fontSize="small" />
                  {car?.doors} {t("transport.doorsLabel")}
                  <EventSeatIcon fontSize="small" />
                  {car?.seats} {t("transport.seatsLabel")}
                  <SettingsIcon fontSize="small" /> {car?.transmission}
                  <LocalGasStationIcon fontSize="small" /> {car?.fuel_type}
                </Box>

                <Divider sx={{ my: 1 }} />

                {/* PRICE + ACTION */}
                <Box
                  sx={{
                    mt: "auto",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                  }}
                >
                  <Box>
                    <Box sx={{ fontSize: 17, fontFamily: "Neutraface2Demi" }}>
                      {car?.currency} {car?.base_price?.toFixed(2)} {t("transport.perDayLabel")}
                    </Box>
                    <Box sx={{ fontSize: 12, color: "text.secondary" }}>
                      {t("transport.totalLabel")} {car?.currency} {car?.final_price?.toFixed(2)}
                    </Box>
                  </Box>

                  <Box sx={{ height: 30 }}>
                    <AppButton
                      label={t("transport.selectButton")}
                      onClick={() => onSelect?.(car)}
                      disableRipple={true}
                      customBackgroundColor={tenantInfo?.style?.button?.color}
                      customActiveColor={tenantInfo?.style?.button?.active}
                      customHoverColor={tenantInfo?.style?.button?.hover}
                      customClickColor={tenantInfo?.style?.button?.click}
                      fontSize="13px"
                      disabled={isDisabled}
                      dataAttribute={`europcar-select-car-button-${car.acriss_code}`}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );
}
